import { z } from 'zod'
import { oc } from '@orpc/contract'
import { BILLING_STATUS_VALUES } from '../../care-coordination/dto/billing-status.contract'
import { billingDetailSchema } from './billing-detail.contract'

// A diagnosis link on a service line being saved. `code` is required (a link with
// no code is meaningless); `system`/`display` round-trip the coding the editor
// loaded so the written Claim keeps a fully-qualified diagnosis coding.
export const billingSaveDiagnosisSchema = z.object({
  code: z.string(),
  display: z.string().nullable(),
  system: z.string().nullable(),
})

// One service line to save. A line with no procedure code is dropped client-side
// before it reaches here, so `code` is required. Modifiers are free-entered (up
// to three); nothing is auto-assigned.
export const billingSaveServiceLineSchema = z.object({
  code: z.string(),
  display: z.string().nullable(),
  system: z.string().nullable(),
  modifiers: z.array(z.string()),
  price: z.number().nullable(),
  quantity: z.number().nullable(),
  diagnoses: z.array(billingSaveDiagnosisSchema),
})

// Save the full set of billing service lines for one encounter. Last-write-wins:
// the service lines fully replace the Claim's items/diagnoses. The handler lazily
// creates the Claim (and its encounter-linking Provenance) when none exists,
// stamps the provider-organization compartment, and records an AuditEvent. When
// `status` is present the encounter's Claim Status tag is set in the same save
// (omit it to leave the status untouched). Returns the fresh billing detail so the
// drawer and dashboard row update without a refetch.
export const billingSave = oc
  .route({ method: 'POST', path: '/v2/billing/save' })
  .input(
    z.object({
      encounterId: z.string(),
      serviceLines: z.array(billingSaveServiceLineSchema),
      status: z.enum(BILLING_STATUS_VALUES).optional(),
      /** PD-1597 — a billing administrator's reason for saving a $0 submitted charge. */
      zeroChargeOverride: z.object({ reason: z.string().trim().min(10) }).optional(),
    }),
  )
  .output(billingDetailSchema)
